<script setup lang="ts">
import { type DataTableRowClickEvent } from "primevue/datatable";
import { type PropType, ref, type Ref } from "vue";
import { ProfileAdministrationAPI } from "../../api";
import StatusIcon from "../participants/StatusIcon.vue";
import { FilterMatchMode } from "@primevue/core/api";
import ProjectRecipients from "./ProjectRecipients.vue";
import RecipientsType from "./RecipientsType.vue";
import { format } from "date-fns";
import { parseServerDate } from "@persolog/utils";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import { Participant, Project } from "../../types/PersologOnlineTypes.ts";

defineProps({
    projects: Object as PropType<Project[] | null>,
});

const { t } = useI18n();
const toast = useToast();

const languageNamesInEnglish = new Intl.DisplayNames(["en"], { type: "language" });

const expandedRows = ref<{ [key: string]: boolean }>({});

const filters = ref({
    id: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    product: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    creation: { value: null, matchMode: FilterMatchMode.DATE_IS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
});
const statuses = ref(["CREATED", "ACTIVATED", "STARTED", "COMPLETED", "CALCULATED", "FILLED", "GENERATED", "NOTIFIED"]);
const participants: Ref<Participant[]> = ref<Participant[]>([]);

function onRowClick(event: DataTableRowClickEvent) {
    const rowId = event.data.id;
    const isExpanded = !!expandedRows.value[rowId];

    if (isExpanded) {
        expandedRows.value = {};
        participants.value = [];
    } else {
        onRowExpand(event); // your existing logic to load participants + set expanded row
    }
}

async function onRowExpand($event: DataTableRowClickEvent) {
    participants.value = [];
    expandedRows.value = { [$event.data.id]: true };
    await reloadParticipants($event.data.id);
}

async function reloadParticipants(projectId: number) {
    try {
        participants.value = await ProfileAdministrationAPI.fetchParticipantsByProject(projectId);
    } catch (error) {
        // Without this the row just stayed empty, which is indistinguishable from a project
        // that genuinely has no participants.
        participants.value = [];
        console.error("Could not load the participants of the project", error);
        toast.add({
            severity: "error",
            summary: t("messages.error"),
            detail: t("messages.load.error"),
            life: 4000,
        });
    }
}
</script>

<template>
    <DataTable
        v-if="projects?.length !== 0"
        v-model:expanded-rows="expandedRows"
        :value="projects"
        selection-mode="single"
        paginator
        :rows="10"
        :rows-per-page-options="[5, 10, 20, 50]"
        data-key="id"
        size="small"
        table-style="width: 100%"
        v-model:filters="filters"
        filter-display="row"
        @rowClick="onRowClick"
    >
        <template #header>
            <div class="flex flex-wrap items-center justify-between gap-2">
                <span class="text-xl text-content font-bold">{{ t("projects.label") }}</span>
            </div>
        </template>
        <Column field="id" :header="t('projects.id')">
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    v-model="filterModel.value"
                    type="text"
                    @input="filterCallback()"
                    class="p-column-filter w-full"
                    :placeholder="t('projects.search', { search: t('projects.id') })"
                />
            </template>
        </Column>
        <Column field="name" :header="t('projects.name')">
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    v-model="filterModel.value"
                    type="text"
                    @input="filterCallback()"
                    class="p-column-filter w-full"
                    :placeholder="t('projects.search', { search: t('projects.name') })"
                />
            </template>
        </Column>
        <Column field="product" :header="t('projects.product')" :sortable="true">
            <template #filter="{ filterModel, filterCallback }">
                <InputText
                    v-model="filterModel.value"
                    type="text"
                    @input="filterCallback()"
                    class="p-column-filter w-full"
                    :placeholder="t('projects.search', { search: t('projects.product') })"
                />
            </template>
        </Column>
        <Column field="language" :header="t('projects.language')">
            <template #body="slotProps">
                {{ languageNamesInEnglish.of(slotProps.data.language.split("_")[0]) }}
            </template>
        </Column>
        <Column :header="t('projects.date')" filter-field="creation" data-type="date" :sortable="true">
            <template #body="slotProps">
                {{ format(parseServerDate(slotProps.data.creation), "d. MMM yyyy") }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <DatePicker
                    v-model="filterModel.value"
                    date-format="d. M yy"
                    placeholder="dd. MM yyyy"
                    mask="99. AAA 9999"
                    fluid
                    @dateSelect="filterCallback"
                />
            </template>
        </Column>
        <Column field="status" :header="t('projects.status')">
            <template #body="slotProps">
                <StatusIcon :status="slotProps.data.status"></StatusIcon>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Select
                    v-model="filterModel.value"
                    @change="filterCallback()"
                    :options="statuses"
                    :placeholder="t('projects.status')"
                    class="p-column-filter w-full"
                    :show-clear="true"
                >
                    <template #option="slotProps">
                        <StatusIcon :status="slotProps.option"></StatusIcon>
                    </template>
                </Select>
            </template>
        </Column>
        <Column field="recipients" :header="t('projects.recipients')">
            <template #body="slotProps">
                <RecipientsType :recipients="slotProps.data.recipients" />
            </template>
        </Column>
        <template #expansion="slotProps">
            <ProjectRecipients
                v-if="participants"
                :project="slotProps.data"
                :participants="participants"
                @reload="reloadParticipants(slotProps.data.id)"
            />
        </template>
    </DataTable>
</template>
